import { Injectable, isDevMode } from '@angular/core'
import { OAuthService } from 'angular-oauth2-oidc';
import { OAuthSettings } from '@common/constants';
import * as AuthActions from '@/store/authentication/authentication.actions';
import { User } from '@/store/authentication/auth.model';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { filter } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class OpenAuthenticationService {
  private static readonly RETURN_URL_KEY = 'launchpad.returnUrl';
  private reauthenticating = false;

  constructor(private oauthService: OAuthService, private store: Store, private router: Router) {
  }

  private logoutChannel = new BroadcastChannel('auth');

  initAuth() {
    this.oauthService.configure({
      issuer: OAuthSettings.issuer,
      redirectUri: OAuthSettings.redirectUriPath.length > 0 ? window.location.origin + '/' + OAuthSettings.redirectUriPath : window.location.origin,
      clientId: OAuthSettings.clientId,
      scope: OAuthSettings.scope,
      responseType: OAuthSettings.responseType,
      requireHttps: OAuthSettings.requireHttps,
      // Debug logs include tokens/claims — keep them out of production builds.
      showDebugInformation: isDevMode(),
      strictDiscoveryDocumentValidation: false,
      sessionChecksEnabled: false,
      // PKCE is enabled by default for code flow in this library
    });

    // If the session can no longer be kept alive silently (e.g. the refresh token also expired after a long
    // idle), re-authenticate cleanly instead of letting the UI keep firing 401s in the background.
    this.oauthService.events
      .pipe(filter((e) =>
        e.type === 'token_refresh_error' ||
        e.type === 'silent_refresh_error' ||
        e.type === 'silent_refresh_timeout'))
      .subscribe(() => this.reauthenticate());

    this.logoutChannel.onmessage = (event) => {
      if (event.data === 'logout') {
        this.handleRemoteLogout();
      }
    };

    // True ONLY when this load is the OAuth redirect-back (the URL carries ?code=…). On a plain refresh it's
    // false — so we must NOT navigate away; the Angular router restores the current URL on its own.
    const isLoginCallback = window.location.search.includes('code=');

    // Return the promise so the APP_INITIALIZER awaits discovery + code-exchange BEFORE routing starts. Without
    // this, the router (and authGuard) run before the token reaches the store → the guard re-triggers login →
    // endless redirect loop.
    return this.oauthService.loadDiscoveryDocumentAndTryLogin().then(() => {
      if (this.oauthService.hasValidAccessToken()) {
        this.oauthService.setupAutomaticSilentRefresh();
        const user = this.userFromClaims(this.oauthService.getIdentityClaims());
        const token = this.oauthService.getAccessToken();
        this.store.dispatch(AuthActions.loginSuccess({ user, token }));

        if (isLoginCallback) {
          // Fresh login → clean the ?code= URL and return to where the user was headed (or the root). On a plain
          // refresh we deliberately do nothing here, so the user stays on the page they refreshed from.
          this.router.navigateByUrl(this.consumeReturnUrl() ?? '/');
        }
      } else {
        // No valid token on load (e.g. a deep-link or an expired session) → remember the target, then sign in.
        this.saveReturnUrl(window.location.pathname + window.location.search);
        this.login();
      }
    });
  }

  login() {
    this.oauthService.initCodeFlow();
  }

  /** Remember the given target route, then redirect to the Identity login. */
  redirectToLogin(returnUrl?: string) {
    if (returnUrl) {
      this.saveReturnUrl(returnUrl);
    }
    this.login();
  }

  /**
   * Token rejected/expired mid-session → clear the dead session and bounce to the Identity login, returning to
   * the current page afterward. Guarded so concurrent 401s trigger only one redirect.
   */
  reauthenticate() {
    if (this.reauthenticating) {
      return;
    }
    this.reauthenticating = true;
    this.saveReturnUrl(this.router.url);
    this.oauthService.logOut(true); // clear local tokens only (no end-session redirect)
    this.store.dispatch(AuthActions.logout());
    this.login(); // redirect to Identity to re-authenticate
  }

  logout() {
    this.logoutLocally();
    this.store.dispatch(AuthActions.logout());
    this.logoutChannel.postMessage('logout');
  }

  /**
   * Reaction in OTHER tabs when one tab logs out (via the BroadcastChannel). Clears this tab's tokens but does
   * NOT hit the end-session endpoint — the initiating tab already did, and the shared-localStorage tokens are
   * gone here, so `logOut()` would redirect with a "Missing id_token_hint". Then force a fresh login
   * (prompt=login) so a still-alive SSO session can't silently sign this tab back in.
   */
  private handleRemoteLogout() {
    if (this.reauthenticating) {
      return;
    }
    this.reauthenticating = true;
    this.oauthService.logOut(true); // clear local tokens only, no end-session redirect
    this.store.dispatch(AuthActions.logout());
    this.oauthService.initCodeFlow('', { prompt: 'login' });
  }

  getFullUserInfo(user: User, token: string) {
    // Once the profile load completes, the OIDC lib has fully processed the tokens — re-publish the resolved
    // claims so the sidebar/topbar populate right after login (not only after a refresh). Prefer the id_token
    // claims (carry email); fall back to the userinfo payload. Don't log out on a profile-load failure.
    this.oauthService
      .loadUserProfile()
      .then((profile: any) => {
        const claims = this.oauthService.getIdentityClaims() ?? profile?.info ?? profile
        this.store.dispatch(AuthActions.setUser({ user: claims ? this.userFromClaims(claims) : user }))
      })
      .catch((error) => console.error('Failed to load user profile', error))
  }

  /**
   * Map the OIDC token claims onto the app's user model: `sub` = the user id, `org_id` = the organization,
   * `name`/`email` as issued by Identity. The email doubles as the username — it is what the user signs
   * in with.
   */
  private userFromClaims(claims: any): User {
    return {
      sub: claims?.sub,
      orgId: claims?.org_id,
      fullname: claims?.name,
      username: claims?.email,
      email: claims?.email,
    }
  }

  private saveReturnUrl(url: string): void {
    // Don't bounce back to a callback/login/root URL — only meaningful in-app routes.
    if (url && url !== '/' && !url.includes('code=')) {
      sessionStorage.setItem(OpenAuthenticationService.RETURN_URL_KEY, url);
    }
  }

  private consumeReturnUrl(): string | null {
    const url = sessionStorage.getItem(OpenAuthenticationService.RETURN_URL_KEY);
    sessionStorage.removeItem(OpenAuthenticationService.RETURN_URL_KEY);
    return url;
  }

  private logoutLocally(){
    this.oauthService.logOut();
  }
}
